import React, { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { Combobox } from '@eika/combobox';
import { Input } from '@eika/input';
import { Loader } from '@eika/loader';

import { BorettslagAndelWeb, EiendomVariant, MatrikkelWeb } from '../../generated-types';
import { useGetAdresseInfo, useGetAdresser, usePoststed } from '../../hooks/useEiendom';
import { useTranslation } from '../../translations';
import { EiendomsVariantFormData } from '../eiendom/eiendomVariant';
import { AndelFellesgjeldFormData } from './andelFellesgjeld';
import TypeBoligInfo from './typeBoligInfo';

export type BoligAdresseFormData = {
    adresse: string;
    postnummer: string;
    poststed: string | null;
    key: string | null;
    andel: BorettslagAndelWeb | null;
    matrikkel: MatrikkelWeb | null;
    fraAdresseSoek: boolean;
};

const BoligAdresse: FC = () => {
    const { kjoepBolig, validationMessage } = useTranslation();
    const {
        register,
        setValue,
        watch,
        formState: { errors },
    } = useFormContext<BoligAdresseFormData>();
    const { setValue: setVariant } = useFormContext<EiendomsVariantFormData>();
    const { data: poststed, isLoading, isFetched } = usePoststed(watch('postnummer') || '');
    const { data: adresser } = useGetAdresser(watch('adresse') || '');
    const { data: adresseInfo, isLoading: loadingInfo, isFetched: fetchedInfo } = useGetAdresseInfo(watch('key') || '');
    const matrikkel = watch('matrikkel');
    const { setValue: setFellesgjeld } = useFormContext<AndelFellesgjeldFormData>();
    const visInfoboks =
        watch('fraAdresseSoek') &&
        watch('poststed') &&
        (adresseInfo?.andeler?.length === 0 || watch('andel')) &&
        (matrikkel || adresseInfo?.eiendomsVariant !== EiendomVariant.SELVEIER) &&
        adresseInfo?.eiendomsVariant !== EiendomVariant.AKSJELEILIGHET;
    const [vetIkke, setVetIkke] = React.useState(false);
    const [soekSeksjon, setSoekSeksjon] = React.useState('');
    const seksjonValue = () => {
        if (matrikkel) {
            return `${matrikkel.seksjonsnummer}`;
        }
        return vetIkke ? 'Vet ikke' : soekSeksjon;
    };

    useEffect((): void => {
        register('poststed', { required: `${validationMessage.invalidPostnummer}` });
    }, [register, validationMessage]);

    useEffect((): void => {
        if (isFetched) {
            setValue('poststed', poststed?.value || null, { shouldValidate: true, shouldDirty: true });
            setValue('key', adresser?.find((adresse) => adresse.postnr === watch('postnummer'))?.key || null);
        }
        if (fetchedInfo && adresseInfo?.eiendomsVariant) {
            setVariant('eiendomVariant', adresseInfo.eiendomsVariant);
        }
        if (
            fetchedInfo &&
            !loadingInfo &&
            isFetched &&
            adresseInfo &&
            adresseInfo.matrikler.length === 1 &&
            adresseInfo.eiendomsVariant !== EiendomVariant.BORETTSANDEL
        ) {
            setValue('matrikkel', adresseInfo.matrikler[0]);
        }
    }, [poststed, setValue, isFetched, fetchedInfo, adresser, watch, loadingInfo, adresseInfo, setVariant]);

    useEffect((): void => {
        register('adresse', { required: `${validationMessage.requiredValue}` });
    }, [register, validationMessage]);

    const setValuesOnSelect = (value: string) => {
        setValue('matrikkel', null);
        setValue('andel', null);
        setValue('key', '');
        setVariant('eiendomVariant', undefined);
        setFellesgjeld('andelFellesgjeld', 0);
        setVetIkke(false);
        const val = value.split(',');
        if (val[0]) setValue('adresse', val[0]);
        if (val[1]) setValue('postnummer', val[1].replace(/\D/g, ''));
        else {
            setValue('postnummer', '');
            setValue('poststed', '');
        }
        setValue(
            'fraAdresseSoek',
            adresser
                ? adresser.filter(
                      (adresse) =>
                          adresse.gatenavn &&
                          adresse.gatenr &&
                          watch('adresse').includes(adresse.gatenavn) &&
                          watch('adresse').includes(adresse.gatenr?.toString()) &&
                          watch('postnummer').includes(adresse.postnr),
                  ).length > 0
                : false,
        );
    };

    const setAndel = (value: string) => {
        setVetIkke(false);
        setValue('fraAdresseSoek', true);
        if (value.includes('Vet ikke')) {
            setValue('fraAdresseSoek', false);
            setVetIkke(true);
        }
        setValue('andel', adresseInfo?.andeler.find((andel) => andel.andelsnummer === value) || null);
        if (!watch('andel')) {
            setValue('andel.andelsnummer', value);
            setValue('fraAdresseSoek', false);
        }
    };

    const setMatrikkel = (value: string) => {
        setVetIkke(false);
        if (value.includes('Vet ikke')) {
            setValue('fraAdresseSoek', false);
            setVetIkke(true);
        }
        setValue('matrikkel', adresseInfo?.matrikler.find((matrikkel) => value === matrikkel.seksjonsnummer) || null);
        if (!watch('matrikkel')) setSoekSeksjon(value);
    };

    return (
        <>
            <Combobox
                id="adresse"
                label={kjoepBolig.labelGateadresse}
                error={errors.adresse?.message?.toString()}
                suggestions={
                    adresser && adresser.length > 0
                        ? adresser.map((adresse) => ({
                              value: `${adresse.gatenavn} ${adresse.gatenr}${adresse.gatebokstav || ''}, ${
                                  adresse.postnr
                              } ${adresse.poststed}`,
                          }))
                        : []
                }
                value={watch('adresse') || ''}
                onChange={(value) => setValue('adresse', value)}
                onSelect={(value) => setValuesOnSelect(value)}
                searchInput
                selectOnBlur={false}
            />

            <div className="column column-gap align-items-end">
                <Input
                    inputMode="numeric"
                    label={kjoepBolig.labelPostnummer}
                    className="max-width-200"
                    error={errors.postnummer?.message?.toString() || errors.poststed?.message?.toString()}
                    {...register('postnummer', {
                        required: `${validationMessage.requiredValue}`,
                        minLength: { value: 4, message: `${validationMessage.postnummerToShort}` },
                        maxLength: { value: 4, message: `${validationMessage.postnummerToLong}` },
                    })}
                />
                {isLoading ? (
                    <span className="max-width-100">
                        <Loader mini />
                    </span>
                ) : (
                    <span style={{ paddingBottom: '11px' }}>{poststed?.value}</span>
                )}
            </div>
            {adresseInfo?.andeler && fetchedInfo && adresseInfo.andeler?.length > 0 ? (
                <Combobox
                    id="andeler"
                    label={kjoepBolig.labelAndel}
                    className="max-width-200"
                    suggestions={
                        adresseInfo.andeler
                            ? [{ value: 'Vet ikke' }].concat(
                                  adresseInfo.andeler.map((andel) => ({
                                      value: `${andel.andelsnummer}`,
                                  })),
                              )
                            : []
                    }
                    error={errors.andel?.message?.toString()}
                    value={watch('andel')?.andelsnummer || (vetIkke ? 'Vet ikke' : '')}
                    onChange={(value) => setAndel(value)}
                    onSelect={(value) => setAndel(value)}
                />
            ) : null}
            {adresseInfo?.matrikler &&
            fetchedInfo &&
            adresseInfo.matrikler?.length > 1 &&
            adresseInfo.andeler.length === 0 ? (
                <Combobox
                    id="seksjon"
                    label={kjoepBolig.labelMatrikkel}
                    className="max-width-200"
                    suggestions={
                        adresseInfo.matrikler
                            ? [{ value: 'Vet ikke' }].concat(
                                  adresseInfo.matrikler.map((matrikkel) => ({
                                      value: `${matrikkel.seksjonsnummer}`,
                                  })),
                              )
                            : []
                    }
                    error={errors.matrikkel?.message?.toString()}
                    value={seksjonValue()}
                    onChange={(value) => setMatrikkel(value)}
                    onSelect={(value) => setMatrikkel(value)}
                />
            ) : null}
            {visInfoboks && (
                <TypeBoligInfo
                    adresse={`${watch('adresse')}, ${watch('postnummer')} ${watch('poststed')}`}
                    matrikkel={matrikkel || undefined}
                    erSelveier={adresseInfo?.andeler?.length === 0}
                    andel={adresseInfo?.andeler?.find((andel) => andel.andelsnummer === watch('andel')?.andelsnummer)}
                />
            )}
        </>
    );
};

export default BoligAdresse;
